import { ChevronDown, ChevronRight, Camera, StickyNote } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { cn } from "@/shared/lib/utils";
import { ConfidenceSlider } from "./ConfidenceSlider";
import { EvidenceUploader } from "./EvidenceUploader";
import { NotesInput } from "./NotesInput";

interface Props {
  fieldId: string;
  propertyId: string;
  surveyId: string | null;
  /** Ids of ancestor node sections, outermost first; used for upload paths. */
  nodePath: string[];
  confidence: number | null | undefined;
  evidence: string[] | undefined;
  notes: string | null | undefined;
  open: boolean;
  onToggle: () => void;
  onChange: (patch: {
    confidence?: number | null;
    evidence?: string[];
    notes?: string | null;
  }) => void;
  onOpenPhotos?: (urls: string[], index: number, title: string) => void;
}

function confidenceTone(v: number): string {
  if (v >= 75) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
  if (v >= 40) return "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30";
  return "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30";
}

export function QuestionMetadataRow({
  fieldId,
  propertyId,
  surveyId,
  nodePath,
  confidence,
  evidence,
  notes,
  open,
  onToggle,
  onChange,
  onOpenPhotos,
}: Props) {
  const hasConfidence = typeof confidence === "number";
  const evidenceCount = evidence?.length ?? 0;
  const hasNotes = !!notes && notes.trim().length > 0;

  return (
    <div className="mt-1 mb-2 ml-4">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-1 -mx-2 rounded text-[11px] text-muted-foreground hover:text-foreground transition"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span>Details</span>
        {hasConfidence && (
          <Badge variant="outline" className={cn("text-[10px] py-0 h-4", confidenceTone(confidence!))}>
            {confidence}%
          </Badge>
        )}
        {evidenceCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px]">
            <Camera className="h-3 w-3" /> {evidenceCount}
          </span>
        )}
        {hasNotes && <StickyNote className="h-3 w-3" />}
      </button>
      {open && (
        <div className="mt-2 space-y-2 pl-5 border-l border-border/40">
          <ConfidenceSlider
            value={confidence}
            onChange={(next) => onChange({ confidence: next })}
          />
          <EvidenceUploader
            propertyId={propertyId}
            surveyId={surveyId}
            nodePath={nodePath}
            fieldId={fieldId}
            value={evidence}
            onChange={(next) => onChange({ evidence: next })}
            onOpenPhotos={onOpenPhotos}
          />
          <NotesInput
            value={notes}
            onChange={(next) => onChange({ notes: next })}
          />
        </div>
      )}
    </div>
  );
}
